import {
  Ban,
  CircleCheck,
  CircleMinus,
  CircleX,
  Clock,
  Loader,
  TriangleAlert,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  AppDeploymentStatus,
  AppStatus,
  DatabaseStatus,
  PodStatus,
  StaticSiteDeploymentStatus,
  StaticSiteStatus,
} from '@/graphql.types'

type StatusType =
  | AppStatus
  | AppDeploymentStatus
  | StaticSiteStatus
  | StaticSiteDeploymentStatus
  | DatabaseStatus
  | PodStatus
  | 'suspended'

type Props = {
  className?: string
  status?: StatusType
}

const getStatusLabel = (status: StatusType | undefined): string => {
  switch (status) {
    case 'success':
    case 'done':
    case 'deploymentSuccess':
    case 'succeeded':
    case 'ready':
    case 'running':
      return 'Success'
    case 'inProgress':
    case 'creating':
    case 'deleting':
    case 'passwordChange':
    case 'deploymentInProgress':
    case 'restoring':
    case 'updating':
    case 'updatingExternalConnection':
    case 'terminating':
      return 'In progress'
    case 'error':
    case 'crashed':
    case 'failed':
    case 'deploymentFailed':
    case 'dbDeletionFailed':
    case 'passwordChangeFailed':
    case 'restoringFailed':
    case 'appDeletionFailed':
      return 'Failed'
    case 'waiting':
      return 'Waiting'
    case 'skipped':
      return 'Skipped'
    case 'cancelled':
    case 'deploymentCancelled':
      return 'Cancelled'
    case 'suspended':
      return 'Suspended'
    default:
      return ''
  }
}

export const StatusIcon = ({ status, className }: Props) => {
  const label = getStatusLabel(status)

  switch (status) {
    case 'success':
    case 'done':
    case 'deploymentSuccess':
    case 'succeeded':
    case 'ready':
    case 'running':
      return (
        <span role="img" aria-label={label}>
          <CircleCheck
            aria-hidden="true"
            className={cn(
              'size-4 fill-emerald-600 stroke-background',
              className
            )}
          />
        </span>
      )
    case 'inProgress':
    case 'creating':
    case 'deleting':
    case 'passwordChange':
    case 'deploymentInProgress':
    case 'restoring':
    case 'updating':
    case 'updatingExternalConnection':
    case 'terminating':
      return (
        <span role="img" aria-label={label}>
          <Loader
            aria-hidden="true"
            className={cn('size-4 animate-spin text-orange-600', className)}
          />
        </span>
      )
    case 'error':
    case 'crashed':
    case 'failed':
    case 'deploymentFailed':
    case 'dbDeletionFailed':
    case 'passwordChangeFailed':
    case 'restoringFailed':
    case 'appDeletionFailed':
      return (
        <span role="img" aria-label={label}>
          <CircleX
            aria-hidden="true"
            className={cn(
              'size-4 fill-destructive stroke-background',
              className
            )}
          />
        </span>
      )
    case 'waiting':
      return (
        <span role="img" aria-label={label}>
          <Clock
            aria-hidden="true"
            className={cn('size-4 stroke-background fill-primary', className)}
          />
        </span>
      )
    case 'skipped':
      return (
        <span role="img" aria-label={label}>
          <CircleMinus
            aria-hidden="true"
            className={cn('size-4 stroke-background fill-primary', className)}
          />
        </span>
      )
    case 'cancelled':
    case 'deploymentCancelled':
      return (
        <span role="img" aria-label={label}>
          <Ban
            aria-hidden="true"
            className={cn('size-4 scale-[0.85]', className)}
          />
        </span>
      )
    case 'suspended':
      return (
        <span role="img" aria-label={label}>
          <TriangleAlert
            aria-hidden="true"
            className={cn('size-4 fill-amber-500 stroke-background', className)}
          />
        </span>
      )
    default:
      return null
  }
}
